import React, { useState } from 'react';
import {
  ModalView,
  Stack,
  Text,
  Button,
  withTranslation,
  Trans,
  ButtonAppearance,
} from '@piwikpro/ui-components';

export interface ConfirmProps {
  messages: Array<string>
  name?: string
  title?: string
  applyBtnText?: string
  applyBtnAppearance?: ButtonAppearance
  cancelBtnText?: string
  onSubmit: (event: React.SyntheticEvent) => void
  onCancel: () => void
  onError?: (error: unknown) => void
  asyncAction?: () => Promise<void>
  t: (key: string, variables?: any) => string
}

export const Confirm: React.FC<ConfirmProps> = ({
  messages,
  name,
  title,
  applyBtnText,
  applyBtnAppearance,
  cancelBtnText,
  onSubmit,
  onCancel,
  onError = () => {},
  asyncAction,
  t,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.SyntheticEvent) => {
    event.preventDefault();

    if (asyncAction) {
      setIsSubmitting(true);
      try {
        await asyncAction();
        onSubmit(event);
      } catch (error) {
        // Modal stays open on error, hide loader and propagate error
        setIsSubmitting(false);
        onError(error);
      }
    } else {
      onSubmit(event);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onCancel();
    }
  };

  return (
    <ModalView
      title={t(title || '', { name })}
      actions={(
        <Stack spacing="narrow" horizontalAlignment="right" verticalAlignment="center">
          <Button
            type="button"
            text={cancelBtnText ? t(cancelBtnText) : t('components:cancel')}
            appearance="default"
            onClick={onCancel}
            disabled={isSubmitting}
          />
          <Button
            type="submit"
            text={applyBtnText ? t(applyBtnText) : t('profile:deleteConfirm.modal.submit')}
            appearance={applyBtnAppearance || 'destructive'}
            onClick={handleSubmit}
            pending={isSubmitting}
            disabled={isSubmitting}
            focused
          />
        </Stack>
      )}
      onClose={handleClose}
    >
      <form onSubmit={handleSubmit}>
        <Stack vertical spacing="narrow">
          {messages?.map((message: string) => (
            <Stack.Item key={message}>
              <Text>
                <Trans
                  i18nKey={t(message, { name })}
                  components={[
                    <b>text</b>,
                    <ul>
                      <li>text</li>
                    </ul>,
                  ]}
                />
              </Text>
            </Stack.Item>
          ))}
        </Stack>
      </form>
    </ModalView>
  );
};

export default withTranslation(['profile', 'components'])(Confirm);
